import { useMemo, type ReactNode } from 'react';

import { buildResults, getFilteredChildren } from '@/features/header/utils/search';
import { useDynamicMenu } from '@/features/navigation/hooks/useDynamicMenu';
import { normalizeString } from '@/utils/string';
import { Compass } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface SearchResult {
  to: string;
  category: string;
  label: string;
  icon: ReactNode;
}

const DYNAMIC_SECTIONS = new Set(['navigation.favorites.title', 'navigation.recent.title']);

export const useSearchResults = (query: string): SearchResult[] => {
  const { t, i18n } = useTranslation();
  const { menu: dynamicMenu } = useDynamicMenu();

  const searchableMenu = useMemo(() => dynamicMenu.filter((item) => !DYNAMIC_SECTIONS.has(item.text)), [dynamicMenu]);

  return useMemo(() => {
    const lowerQuery = normalizeString(query.trim());
    if (!lowerQuery) return [];

    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escapeRegex(lowerQuery)}\\w*`, 'i');

    return searchableMenu.flatMap((menuItem) => {
      if (!menuItem.children) return [];

      const menuItemLabel = t(menuItem.text);
      const menuItemIcon = menuItem.icon ?? <Compass className="size-4" />;

      if (regex.test(normalizeString(menuItemLabel))) {
        return buildResults(menuItemLabel, menuItemIcon, menuItem.children);
      }

      return buildResults(menuItemLabel, menuItemIcon, getFilteredChildren(menuItem, regex));
    });
  }, [query, i18n.language, searchableMenu]);
};
